import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { Upload, Globe, CreditCard, Zap, Euro, Shield } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCalculateBanner728x90Price } from '@/hooks/useBanners728x90';

interface Banner728x90DialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const Banner728x90Dialog: React.FC<Banner728x90DialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { toast } = useToast();
  
  const [months, setMonths] = useState(1);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [monthlyPrice, setMonthlyPrice] = useState(50);

  const calculatePriceMutation = useCalculateBanner728x90Price();

  // CORRECTION: Ne charger le prix QUE quand le dialog est ouvert
  useEffect(() => {
    if (isOpen && user) {
      fetchMonthlyPrice();
      if (months > 0) {
        calculatePrice();
      }
    }
  }, [isOpen, user, months]);

  const fetchMonthlyPrice = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'banner_728x90_monthly_price_cents')
        .maybeSingle();

      if (error) {
        console.error('Error fetching monthly price:', error);
        toast({
          title: 'Erreur de configuration',
          description: 'La base de données n\'est pas encore prête. Veuillez réessayer dans quelques instants.',
          variant: 'destructive',
        });
        return;
      }
      
      const priceInCents = data?.setting_value ? parseInt(String(data.setting_value)) : 5000;
      setMonthlyPrice(priceInCents / 100);
    } catch (error: any) {
      console.error('Error fetching monthly price:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de charger le prix',
        variant: 'destructive',
      });
    }
  };

  const calculatePrice = async () => {
    // CORRECTION: Ne plus appeler la fonction RPC, calculer localement
    try {
      setTotalPrice(monthlyPrice * months);
    } catch (error: any) {
      console.error('Error calculating price:', error);
      setTotalPrice(monthlyPrice * months);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `ad-spot-${Date.now()}.${fileExt}`;
    const filePath = `advertising/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('server-images')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('server-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Erreur',
        description: 'Seules les images sont acceptées',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Erreur',
        description: 'L\'image ne doit pas dépasser 5 MB',
        variant: 'destructive',
      });
      return;
    }

    setImageFile(file);
    setImageUrl('');
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setImageUrl(url);
    setImageFile(null);
    setImagePreview(url);
  };

  const handleSubmit = async () => {
    if (!imageFile && !imageUrl) {
      toast({
        title: 'Image manquante',
        description: 'Veuillez uploader une image ou saisir une URL d\'image',
        variant: 'destructive',
      });
      return;
    }

    if (months < 1) {
      toast({
        title: 'Durée invalide',
        description: 'Minimum 1 mois',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const finalImageUrl = imageFile ? await uploadImage(imageFile) : imageUrl;

      const { data, error } = await supabase.functions.invoke('create-ad-spot-payment', {
        body: {
          months,
          image_url: finalImageUrl,
          website_url: websiteUrl || null,
        },
      });

      if (error) throw error;

      if (data.url) {
        window.open(data.url, '_blank');
        
        toast({
          title: 'Redirection vers le paiement',
          description: 'Vous allez être redirigé vers Stripe pour finaliser votre réservation',
        });

        onSuccess();
      }
    } catch (error: any) {
      console.error('Error creating banner:', error);
      toast({
        title: 'Erreur lors de la réservation',
        description: error.message || 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminAdd = async () => {
    if (!imageFile && !imageUrl) {
      toast({
        title: 'Erreur',
        description: 'Veuillez ajouter une image',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const finalImageUrl = imageFile ? await uploadImage(imageFile) : imageUrl;

      const now = new Date();
      const startDate = now.toISOString().split('T')[0];
      const endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + months);
      const endDateStr = endDate.toISOString().split('T')[0];

      const { error } = await supabase
        .from('banners_728x90')
        .insert({
          user_id: user!.id,
          image_url: finalImageUrl,
          website_url: websiteUrl || null,
          start_date: startDate,
          end_date: endDateStr,
          total_price_cents: Math.round(totalPrice * 100),
          status: 'active',
        });

      if (error) throw error;

      toast({
        title: 'Bannière créée',
        description: 'La bannière a été ajoutée avec succès',
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating admin banner:', error);
      toast({
        title: 'Erreur lors de la création',
        description: error.message || 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setMonths(1);
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    setWebsiteUrl('');
    setTotalPrice(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Réserver une bannière 728×90
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Months selector */}
          <div>
            <Label>Durée (en mois)</Label>
            <Input
              type="number"
              min="1"
              max="12"
              value={months}
              onChange={(e) => setMonths(Math.max(1, parseInt(e.target.value) || 1))}
              className="mt-2"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Prix mensuel : {monthlyPrice}€
            </p>
          </div>

          {/* Image upload/URL */}
          <div>
            <Label className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Image de la bannière (728×90px recommandé)
            </Label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-2"
              disabled={!!imageUrl}
            />
            <p className="text-sm text-muted-foreground mt-1">ou</p>
            <Input
              type="url"
              placeholder="URL de l'image"
              value={imageUrl}
              onChange={handleImageUrlChange}
              className="mt-2"
              disabled={!!imageFile}
            />
            {imagePreview && (
              <div className="mt-4 border rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  className="w-full h-auto max-w-[728px] mx-auto"
                />
              </div>
            )}
          </div>

          {/* Website URL */}
          <div>
            <Label className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              URL de destination (optionnel)
            </Label>
            <Input
              type="url"
              placeholder="https://example.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="mt-2"
            />
          </div>

          {/* Price summary */}
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Prix unitaire</span>
              <span className="font-bold">{monthlyPrice}€/mois</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Durée</span>
              <span className="font-bold">{months} mois</span>
            </div>
            <div className="border-t border-slate-700 pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold">Total</span>
                <span className="text-2xl font-bold text-primary flex items-center gap-1">
                  <Euro className="w-5 h-5" />
                  {totalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Annuler
            </Button>
            
            {isAdmin && (
              <Button
                onClick={handleAdminAdd}
                disabled={isLoading}
                variant="secondary"
                className="flex-1"
              >
                <Shield className="w-4 h-4 mr-2" />
                Ajouter Admin
              </Button>
            )}
            
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex-1"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {isLoading ? 'Chargement...' : `Payer ${totalPrice.toFixed(2)}€`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};